import {
  memo, useRef, useCallback, useEffect, useLayoutEffect, useMemo, useState, type CSSProperties,
} from 'react'
import {
  DndContext, DragEndEvent, DragMoveEvent, DragStartEvent,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import type { ScheduleItem } from '../../types/index.js'
import {
  useStore, useItems, useConflicts, useAgendaView, useZoomAnchor, useHasPermission,
} from '../../store/index.js'
import { useScheduleActions } from '../../hooks/useSchedule.js'
import { AgendaBlock } from './AgendaBlock.js'
import { AgendaMarkerGutter } from './AgendaMarkerGutter.js'
import { AgendaTimeline, AgendaGridLines, NextDayBand, NowLine } from './AgendaTimeline.js'
import {
  useAgendaLayout, resolveDropStartMs, snapMinutesForPpm, magnetSecForPpm, clampPpm, dayY,
  scrollTopForZoom, timelineHours, displayHours, ppmToSlider, sliderToPpm, PPM_MIN, PPM_MAX,
} from './useAgendaLayout.js'
import { ClockChangeBands, ClockChangeGapNotes } from './ClockChangeLayer.js'
import { clockChangeOf } from '../../lib/clock-change.js'
import {
  broadcastClockLabel, broadcastOffsetHours, startOfBroadcastDay,
} from '../../lib/broadcast-day.js'
import { getServerNow, getServerNowMs } from '../../hooks/useServerClock.js'
import { liveEndResizable, resolveLiveEndResize } from './liveResize.js'
import { useOnAirState } from '../../hooks/useOnAirState.js'
import type { GridSelectMode } from './useGridSelection.js'
import {
  ADJACENCY_TOLERANCE_MS, infoserviceInsertPlansAt, infoserviceRuns, resolveInfoserviceResize,
  type InfoserviceInsertPlan, type InfoserviceResizeEdge,
} from './infoserviceResize.js'
import { agendaEdgeHasGap } from './edgeAction.js'
import { nearestEndBelow, nearestStartAbove, type AddGap } from './gapOverflow.js'
import { resolveGroupDropDeltaMs } from './groupDrag.js'

/** Statuts qu'aucun glisser ne déplace : l'antenne a déjà tranché. */
const FROZEN_STATUSES = new Set(['ON_AIR', 'DONE', 'LOADING'])

// ─── Props ────────────────────────────────────────────────────────────────────

interface AgendaCanvasProps {
  selectedItemId: string | null
  /** `mode` porte les modificateurs du clic (voir `useGridSelection`). */
  onSelectItem:   (item: ScheduleItem | null, mode: GridSelectMode) => void
  /** Appartenance à la sélection multiple, interrogée bloc par bloc. */
  isMultiSelected: (id: string) => boolean
  /**
   * Sélection multiple courante. Saisir l'un de ses éléments déplace tout le
   * groupe ; saisir un élément hors sélection la remplace, comme avant.
   */
  multiSelectedIds?: string[]
  onItemContextMenu?: (item: ScheduleItem, x: number, y: number) => void
  onAddAtTime?:   (isoDateString: string, mode: 'START' | 'END', plans?: InfoserviceInsertPlan[], gap?: AddGap) => void
  /** Interdit le glisser-deposer. L'ajout, lui, depend de `onAddAtTime`. */
  dragLocked?:    boolean
  /**
   * Autorise l'insertion aux intersections occupées. Elle raccourcit un
   * infoservice en amont ou en aval et décale la séquence qui l'en sépare :
   * elle demande donc le droit de déplacer, mais
   * elle s'arme comme l'ajout — le cadenas fermé ne la ferme pas à lui seul,
   * sinon le geste resterait invisible dans la vue par défaut.
   */
  canInsert?:     boolean
  /** Instant à amener dans le champ visible. Change de valeur = nouveau saut. */
  focusRequest?:  { iso: string; seq: number } | null
  /**
   * Fin de l'affichage quand la journée se prolonge sur la nuit, jusqu'au
   * premier élément du lendemain (`lib/night-tail.ts`).
   */
  displayEndMs?:  number | null
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const AgendaCanvas = memo(function AgendaCanvas({ selectedItemId, onSelectItem, isMultiSelected, multiSelectedIds, onItemContextMenu, onAddAtTime, dragLocked = false, canInsert = false, focusRequest = null, displayEndMs = null }: AgendaCanvasProps) {
  const items         = useItems()
  const conflicts     = useConflicts()
  const { nextItem }  = useOnAirState()
  const agendaView    = useAgendaView()
  const { setDraggingItem, clearConflicts } = useStore()
  const { moveItem, moveGroup, resizeInfoservice, resizeLive }  = useScheduleActions()
  const canControlPlayout = useHasPermission('PLAYOUT_CONTROL')
  const scrollRef     = useRef<HTMLDivElement>(null)
  const canvasRef     = useRef<HTMLDivElement>(null)
  // Membres d'un glisser de groupe, hors élément saisi (dnd-kit translate
  // déjà celui-là). Posé au départ et à la dépose seulement : pendant le geste,
  // l'écart passe par une variable CSS, sans rendu React à chaque image.
  const [groupDragIds, setGroupDragIds] = useState<ReadonlySet<string> | null>(null)
  const zoomAnchor    = useZoomAnchor()

  const { layoutItems, totalHeight, nowLineY, conflictSet, pxPerHour } =
    useAgendaLayout(items, agendaView.pxPerMinute, agendaView.date, conflicts, displayEndMs)
  // Heures dessinées, nuit comprise : c'est sur elles que se bornent le zoom
  // et les poignées d'infoservice.
  const shownHours = displayHours(agendaView.date, displayEndMs)
  const infoserviceRunsByItem = useMemo(() => infoserviceRuns(items), [items])

  // Remonter la chaîne collée coûte un balayage de la journée : on ne le refait
  // pas à chaque rendu pour une intersection déjà étudiée. Le cache meurt avec
  // la liste d'items, donc à chaque modification de la grille.
  const insertPlansAt = useMemo(() => {
    const cache = new Map<number, InfoserviceInsertPlan[]>()
    return (boundaryMs: number) => {
      const known = cache.get(boundaryMs)
      if (known) return known
      const plans = infoserviceInsertPlansAt(items, infoserviceRunsByItem, boundaryMs, ADJACENCY_TOLERANCE_MS)
      cache.set(boundaryMs, plans)
      return plans
    }
  }, [items, infoserviceRunsByItem])

  // Descripteur de trou mis en cache lui aussi : le bloc étant mémoïsé, il faut
  // que des bornes identiques rendent le même objet d'un rendu à l'autre.
  const gapAt = useMemo(() => {
    const cache = new Map<string, AddGap | null>()
    return (startMs: number, endMs: number, edgeEndAt: number, edgeStartAt: number) => {
      const key = `${startMs}:${endMs}`
      const known = cache.get(key)
      if (known !== undefined) return known
      const plans = [
        insertPlansAt(edgeEndAt).find(plan => plan.edge === 'END'),
        insertPlansAt(edgeStartAt).find(plan => plan.edge === 'START'),
      ].filter(Boolean) as InfoserviceInsertPlan[]
      const gap: AddGap = { startMs, endMs, plans }
      cache.set(key, gap)
      return gap
    }
  }, [insertPlansAt])

  // Dernier défilement observé, relevé *pendant* que l'échelle qui l'accompagne
  // est encore celle du DOM. C'est la seule mesure fiable : une fois la nouvelle
  // hauteur appliquée, `scrollTop` a déjà pu être écrêté par le navigateur.
  const scrollStateRef = useRef<{ top: number; pxPerHour: number } | null>(null)
  // Hauteur de référence du prochain zoom dans la fenêtre, quand le geste en
  // désigne une — la position du pointeur à la molette. `null` le reste du
  // temps : le curseur de la barre d'outils ne montre aucun instant.
  const anchorRatioRef = useRef<number | null>(null)

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (el) scrollStateRef.current = { top: el.scrollTop, pxPerHour: clampPpm(agendaView.pxPerMinute) * 60 }
  }, [agendaView.pxPerMinute])

  // ── Ajustement du scroll au changement de zoom ─────────────────────────────
  //
  // `useLayoutEffect` se déclenche après le rendu DOM mais avant que le
  // navigateur peigne : la nouvelle hauteur est déjà appliquée, on repositionne
  // donc avant que l'opérateur ne voie quoi que ce soit sauter.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const newPxPerHour  = clampPpm(agendaView.pxPerMinute) * 60
    const previous      = scrollStateRef.current
    const pointerRatio  = anchorRatioRef.current
    anchorRatioRef.current = null

    const settle = (top: number) => {
      el.scrollTop = top
      // On réinscrit nous-mêmes l'état : l'événement `scroll` du navigateur
      // arrive de façon asynchrone, et d'ici là un second cran de zoom aurait
      // déjà lu une paire incohérente.
      scrollStateRef.current = { top: el.scrollTop, pxPerHour: newPxPerHour }
    }

    // Premier rendu : on positionne sur "maintenant" pour que l'opérateur
    // ouvre l'agenda directement sur l'heure courante.
    if (previous == null) {
      const nowY = dayY(getServerNow(), agendaView.date, newPxPerHour)
      settle(Math.max(0, nowY - el.clientHeight / 2))
      return
    }

    // Pas de changement réel de densité (autre prop a déclenché le layout) → on ne touche à rien.
    if (previous.pxPerHour === newPxPerHour) return

    // ── Recentrer sur l'heure actuelle (préférence de l'opérateur) ──────────
    // La molette y échappe : elle a désigné un instant, et le respecter est
    // tout l'intérêt du geste.
    if (zoomAnchor === 'now' && pointerRatio === null) {
      const nowY = dayY(getServerNow(), agendaView.date, newPxPerHour)
      settle(Math.max(0, nowY - el.clientHeight / 2))
      return
    }

    // ── Conserver l'instant regardé (par défaut) ────────────────────────────
    settle(scrollTopForZoom(
      previous.top, el.clientHeight, previous.pxPerHour, newPxPerHour,
      shownHours * newPxPerHour, pointerRatio ?? 0.5,
    ))
    // Une nuit qui change sans zoom ne redéfile rien : le garde d'échelle
    // ci-dessus sort avant.
  }, [agendaView.pxPerMinute, agendaView.date, zoomAnchor, shownHours])

  // ── Zoom à la molette, ancré sous le pointeur ───────────────────────────────
  //
  // Le geste le plus direct pour changer d'échelle, et le seul qui désigne un
  // instant : l'heure sous le curseur ne bouge pas. Écouteur manuel car React
  // pose `wheel` en passif, où `preventDefault` — qui empêche ici le zoom de
  // page du navigateur — est ignoré.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      const rect = el.getBoundingClientRect()
      anchorRatioRef.current = rect.height > 0
        ? Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
        : 0.5
      // Un cran de molette vaut un vingtième de la piste du curseur : assez
      // pour avancer, assez peu pour viser. `deltaY` est négatif vers le haut,
      // et un pavé tactile en envoie de tout petits — d'où le signe seul.
      const store = useStore.getState()
      const position = ppmToSlider(store.agendaView.pxPerMinute, PPM_MIN, PPM_MAX)
      store.setPxPerMinute(sliderToPpm(position + (event.deltaY < 0 ? 50 : -50), PPM_MIN, PPM_MAX))
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  // ── Saut vers un instant précis (« Voir » du bandeau de trous) ─────────────
  // Le seq permet de redemander le même instant : sans lui, un second clic sur
  // « Voir » après avoir fait défiler la timeline ne ferait rien.
  //
  // Le saut n'a lieu qu'au clic, jamais parce que l'echelle a change. Sans ce
  // garde-fou, chaque cran de molette rejouait le saut : le zoom ramenait la
  // vue sur le trou et la timeline paraissait bloquee la, alors que l'echelle
  // changeait bel et bien dessous.
  const focusSeqRef = useRef(0)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !focusRequest || focusRequest.seq === focusSeqRef.current) return
    focusSeqRef.current = focusRequest.seq
    const y = dayY(focusRequest.iso, agendaView.date, pxPerHour)
    el.scrollTo({ top: Math.max(0, y - el.clientHeight / 3), behavior: 'smooth' })
  }, [focusRequest, pxPerHour, agendaView.date])

  // ── Nettoyage des conflits
  useEffect(() => {
    if (conflicts.length === 0) return
    const t = setTimeout(() => clearConflicts(), 3_000)
    return () => clearTimeout(t)
  }, [conflicts, clearConflicts])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: dragLocked ? { distance: Infinity } : { distance: 4 },
    })
  )

  /** Éléments que le glisser de `activeId` emporte, lui compris ; `null` = glisser simple. */
  const groupFor = useCallback((activeId: string): string[] | null => {
    if (!multiSelectedIds || multiSelectedIds.length < 2 || !multiSelectedIds.includes(activeId)) return null
    const byId = new Map(items.map(item => [item.id, item]))
    const movable = multiSelectedIds.filter(id => {
      const item = byId.get(id)
      return item !== undefined && !FROZEN_STATUSES.has(item.status)
    })
    return movable.length >= 2 ? movable : null
  }, [items, multiSelectedIds])

  const setGroupOffset = useCallback((y: number) => {
    canvasRef.current?.style.setProperty('--agenda-group-drag-y', `${y}px`)
  }, [])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (dragLocked) return
    const activeId = event.active.id as string
    setDraggingItem(activeId)
    const group = groupFor(activeId)
    if (group) {
      // La sélection reste en place : c'est elle qu'on déplace.
      setGroupOffset(0)
      setGroupDragIds(new Set(group.filter(id => id !== activeId)))
      return
    }
    onSelectItem(null, 'REPLACE')
  }, [setDraggingItem, onSelectItem, dragLocked, groupFor, setGroupOffset])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    // `delta` inclut déjà le défilement de la grille pendant le geste : c'est
    // exactement la translation que dnd-kit applique à l'élément saisi (sans
    // DragOverlay, il y ajoute le défilement). Les autres membres, posés dans le
    // même contenu défilant, prennent la même valeur telle quelle — retrancher
    // le défilement les décollerait de l'élément saisi (vérifié à l'écran).
    if (groupDragIds) setGroupOffset(event.delta.y)
  }, [groupDragIds, setGroupOffset])

  const handleDragCancel = useCallback(() => {
    setDraggingItem(null)
    setGroupDragIds(null)
    setGroupOffset(0)
  }, [setDraggingItem, setGroupOffset])

  const handleGroupDrop = useCallback(async (activeId: string, group: string[], deltaY: number) => {
    const members = layoutItems.filter(li => group.includes(li.item.id))
    const anchor = members.find(li => li.item.id === activeId)
    if (!anchor || members.length === 0) return

    const groupSet = new Set(group)
    const deltaMs = resolveGroupDropDeltaMs({
      members: members.map(li => ({
        startMs: new Date(li.item.startTime).getTime(),
        endMs:   new Date(li.item.endTime).getTime(),
      })),
      anchorStartMs: new Date(anchor.item.startTime).getTime(),
      rawDeltaMs:    (deltaY / pxPerHour) * 3_600_000,
      // Les éléments engagés à l'antenne, sélectionnés ou non, sont des voisins :
      // ils restent en place et le groupe ne se pose jamais dessus.
      neighbours: layoutItems
        .filter(li => !groupSet.has(li.item.id))
        .map(li => ({
          id:      li.item.id,
          startMs: new Date(li.item.startTime).getTime(),
          endMs:   new Date(li.item.endTime).getTime(),
        })),
      dayStartMs:  startOfBroadcastDay(agendaView.date).getTime(),
      snapMinutes: snapMinutesForPpm(agendaView.pxPerMinute),
      magnetMs:    magnetSecForPpm(agendaView.pxPerMinute) * 1000,
    })

    if (deltaMs === null) {
      useStore.getState().pushToast(
        'Aucune place libre à cet endroit pour toute la sélection : elle reste à son horaire.',
        'warning',
        6000,
      )
      return
    }
    if (deltaMs === 0) return

    try {
      const result = await moveGroup(group, deltaMs)
      if (result.skippedIds.length > 0) {
        useStore.getState().pushToast(
          `${result.skippedIds.length} élément(s) déjà à l’antenne sont restés en place.`,
          'warning',
          6000,
        )
      }
    } catch (err) {
      console.error('[AgendaCanvas] moveGroup failed:', err)
      useStore.getState().pushToast(
        err instanceof Error ? err.message : 'Impossible de déplacer la sélection.',
        'warning',
        6000,
      )
    }
  }, [layoutItems, pxPerHour, agendaView.date, agendaView.pxPerMinute, moveGroup])

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setDraggingItem(null)
    const wasGroup = groupDragIds !== null
    setGroupDragIds(null)
    setGroupOffset(0)
    if (dragLocked) return

    const { active, delta } = event
    if (!delta.y || Math.abs(delta.y) < 4) return

    if (wasGroup) {
      const group = groupFor(active.id as string)
      if (group) await handleGroupDrop(active.id as string, group, delta.y)
      return
    }

    const original = layoutItems.find(li => li.item.id === active.id)
    if (!original) return

    // Le geste est la seule grandeur qui parte du pixel ; tout le reste se
    // résout sur les horaires réels. Passer par la géométrie des blocs faisait
    // dériver la dépose d'une demi-minute une fois la grille dézoomée, assez
    // pour poser un jingle à cheval sur le programme qu'il devait précéder.
    const startMs    = new Date(original.item.startTime).getTime()
    const durationMs = new Date(original.item.endTime).getTime() - startMs
    const deltaMs    = (delta.y / pxPerHour) * 3_600_000

    // Snap progressif : plus l'échelle est fine, plus le snap l'est aussi.
    // Indispensable en haut de plage pour placer précisément un spot de 10 s.
    const snapMin = snapMinutesForPpm(agendaView.pxPerMinute)

    // Bornes réelles, non rognées à la journée : un film commencé la veille
    // occupe l'antenne même si la grille ne montre que sa fin.
    const neighbours = layoutItems
      .filter(li => li.item.id !== original.item.id)
      .map(li => ({
        id:      li.item.id,
        startMs: new Date(li.item.startTime).getTime(),
        endMs:   new Date(li.item.endTime).getTime(),
      }))

    // Aimantage aux bords voisins, puis garantie de non-recouvrement : un
    // programme ne se pose jamais par-dessus un autre.
    const droppedMs = resolveDropStartMs(
      startMs + deltaMs, durationMs, neighbours,
      startOfBroadcastDay(agendaView.date).getTime(),
      snapMin, magnetSecForPpm(agendaView.pxPerMinute) * 1000,
    )

    if (droppedMs === null) {
      useStore.getState().pushToast(
        'Aucune place libre à cet endroit : le programme reste à son horaire.',
        'warning',
        6000,
      )
      return
    }

    if (droppedMs === startMs) return

    try {
      await moveItem(active.id as string, new Date(droppedMs).toISOString())
    } catch (err) {
      console.error('[AgendaCanvas] moveItem failed:', err)
      useStore.getState().pushToast(
        err instanceof Error ? err.message : 'Impossible de déplacer cette partie.',
        'warning',
        6000,
      )
    }
  }, [layoutItems, pxPerHour, agendaView.date, agendaView.pxPerMinute, moveItem, setDraggingItem, dragLocked, groupDragIds, setGroupOffset, groupFor, handleGroupDrop])

  const handleInfoserviceResize = useCallback(async (
    item: ScheduleItem,
    edge: InfoserviceResizeEdge,
    deltaPx: number,
  ) => {
    if (dragLocked) return
    const run = infoserviceRunsByItem.get(item.id)
    if (!run) return

    const memberIds = new Set(run.memberIds)
    const neighbours = items
      .filter(candidate => !memberIds.has(candidate.id) && candidate.status !== 'SKIPPED')
      .map(candidate => ({
        startMs: new Date(candidate.startTime).getTime(),
        endMs: new Date(candidate.endTime).getTime(),
      }))
    const day = startOfBroadcastDay(agendaView.date).getTime()
    const resized = resolveInfoserviceResize({
      run,
      edge,
      deltaMs: (deltaPx / pxPerHour) * 3_600_000,
      neighbours,
      dayStartMs: day,
      // Jusqu'au premier élément du lendemain : la plage peut passer minuit.
      dayEndMs: day + shownHours * 3_600_000,
      snapMinutes: snapMinutesForPpm(agendaView.pxPerMinute),
      magnetMs: magnetSecForPpm(agendaView.pxPerMinute) * 1_000,
    })
    if (resized.startMs === run.startMs && resized.endMs === run.endMs) return

    try {
      await resizeInfoservice(
        item.id,
        new Date(resized.startMs).toISOString(),
        new Date(resized.endMs).toISOString(),
        run.memberIds,
      )
    } catch (error) {
      console.error('[AgendaCanvas] resizeInfoservice failed:', error)
      useStore.getState().pushToast(
        error instanceof Error ? error.message : 'Impossible de redimensionner l’infoservice.',
        'warning',
        6000,
      )
    }
  }, [agendaView.date, agendaView.pxPerMinute, dragLocked, infoserviceRunsByItem, items, pxPerHour, resizeInfoservice, shownHours])

  // Fin d'un direct tirée comme celle d'un infoservice : il prend sur
  // l'infoservice/interlude qui suit, s'arrête net sur tout autre élément
  // (`liveResize.ts`, règle serveur `lib/live-end-resize-plan.ts`).
  const handleLiveResize = useCallback(async (
    item: ScheduleItem,
    edge: InfoserviceResizeEdge,
    deltaPx: number,
  ) => {
    if (dragLocked || edge !== 'END' || !liveEndResizable(item)) return
    const live = {
      startMs: new Date(item.startTime).getTime(),
      endMs:   new Date(item.endTime).getTime(),
      status:  item.status,
    }
    const neighbours = items
      .filter(candidate => candidate.id !== item.id && candidate.status !== 'SKIPPED')
      .map(candidate => ({
        itemType: candidate.itemType,
        status:   candidate.status,
        startMs:  new Date(candidate.startTime).getTime(),
        endMs:    new Date(candidate.endTime).getTime(),
        infoserviceSourceId: candidate.adPlacements?.[0]?.spot?.id ?? null,
      }))
    const day = startOfBroadcastDay(agendaView.date).getTime()
    const endMs = resolveLiveEndResize({
      live,
      deltaMs:     (deltaPx / pxPerHour) * 3_600_000,
      neighbours,
      nowMs:       getServerNowMs(),
      dayStartMs:  day,
      dayEndMs:    day + shownHours * 3_600_000,
      snapMinutes: snapMinutesForPpm(agendaView.pxPerMinute),
      magnetMs:    magnetSecForPpm(agendaView.pxPerMinute) * 1_000,
    })
    if (endMs === live.endMs) return

    try {
      await resizeLive(item.id, new Date(endMs).toISOString())
      if (item.status === 'ON_AIR') {
        const clock = new Date(endMs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
        useStore.getState().pushToast(`Fin du direct réglée à ${clock}.`, 'success', 5000)
      }
    } catch (error) {
      console.error('[AgendaCanvas] resizeLive failed:', error)
      useStore.getState().pushToast(
        error instanceof Error ? error.message : 'Impossible d’ajuster la fin du direct.',
        'warning',
        6000,
      )
    }
  }, [agendaView.date, agendaView.pxPerMinute, dragLocked, items, pxPerHour, resizeLive, shownHours])

  // La gouttiere et la colonne se partagent la liste : un element trop court
  // pour porter son texte quitte la colonne au lieu de se poser dessus.
  const blocks  = layoutItems.filter(li => !li.isMarker)
  const markers = layoutItems.filter(li => li.isMarker)

  const handleCanvasClick = useCallback(() => onSelectItem(null, 'REPLACE'), [onSelectItem])

  // Heure portée par l'étiquette de la ligne MAINTENANT. La minute suffit :
  // la seconde changerait le libellé en permanence sans rien apprendre.
  // Heure d'antenne, pas heure du poste : c'est celle que la regie lit.
  const nowLabel = broadcastClockLabel(agendaView.date, broadcastOffsetHours(getServerNow(), agendaView.date))
  const visibleDayStartMs = startOfBroadcastDay(agendaView.date).getTime()
  const visibleDayEndMs = visibleDayStartMs + shownHours * 3_600_000
  const midnightY = timelineHours(agendaView.date) * pxPerHour
  const clockChange = clockChangeOf(agendaView.date)

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div ref={canvasRef} className="agenda-canvas">
        <div
          ref={scrollRef}
          className="agenda-canvas__scroll"
          onScroll={handleScroll}
          aria-label="Planning défilable"
          role="region"
          tabIndex={0}
        >
          <AgendaTimeline
            pxPerHour={pxPerHour}
            nowLineY={nowLineY}
            totalHeight={totalHeight}
            date={agendaView.date}
            nextDayY={totalHeight > midnightY ? midnightY : null}
          />

          <AgendaMarkerGutter
            markers={markers}
            totalHeight={totalHeight}
            selectedItemId={selectedItemId}
            isMultiSelected={isMultiSelected}
            groupDragIds={groupDragIds}
            onSelect={onSelectItem}
            onContextMenu={onItemContextMenu}
            dragLocked={dragLocked}
          />

          <div
            className="agenda-canvas__content"
            style={{ '--timeline-height': `${totalHeight}px` } as CSSProperties}
            onClick={handleCanvasClick}
          >
            <AgendaGridLines pxPerHour={pxPerHour} totalHeight={totalHeight} date={agendaView.date} />
            <NextDayBand top={midnightY} height={totalHeight - midnightY} />
            {clockChange && <ClockChangeBands change={clockChange} pxPerHour={pxPerHour} />}
            <ClockChangeGapNotes change={clockChange} date={agendaView.date} items={items} pxPerHour={pxPerHour} />

            {blocks.map(li => {
              const liStart = new Date(li.item.startTime).getTime()
              const liEnd = new Date(li.item.endTime).getTime()
              // Un écart mesurable est un trou, même de quelques secondes :
              // c'est ainsi qu'on réserve la place d'un jingle. Seuls les bords
              // réellement collés portent le geste « Insérer ».
              const hasGapBefore = agendaEdgeHasGap(items, li.item.id, liStart, 'endTime')
              const hasGapAfter = agendaEdgeHasGap(items, li.item.id, liEnd, 'startTime')
              // Direct : poignée de fin (pas de début, le départ reste celui
              // de la grille). À l'antenne, le geste déplace la coupure : il
              // demande aussi le droit de pilotage.
              const liveResize = !dragLocked
                && liveEndResizable(li.item)
                && (li.item.status !== 'ON_AIR' || canControlPlayout)
                && liEnd <= visibleDayEndMs
              const infoRun = !dragLocked && li.item.itemType === 'INFOSERVICE'
                ? infoserviceRunsByItem.get(li.item.id) ?? null
                : null

              // Chaque intersection cherche un plan d'insertion. Les plans
              // indiquent les infoservices atteignables en amont et en aval ;
              // sans plan, le bord conserve le geste ordinaire « Ajouter ».
              // Quand une réserve existe, programmes et habillage du côté choisi
              // se décalent pour ouvrir exactement la durée ajoutée :
              // c'est là que se glissent les spots et les « coming next »
              // décidés au dernier moment. L'intersection porte deux boutons,
              // un par bloc qu'elle sépare — le survol n'en montre jamais qu'un.
              const insertBefore = canInsert && !hasGapBefore ? insertPlansAt(liStart) : null
              // Le bord bas d'une plage d'infoservice porte déjà sa poignée de
              // redimensionnement : on n'y superpose pas un second bouton.
              const insertAfter = canInsert && !hasGapAfter && li.item.itemType !== 'INFOSERVICE'
                ? insertPlansAt(liEnd)
                : null

              // Quand un trou existe, on borne aussi son autre extrémité : l'ajout
              // qui dépasse le trou devra rendre le surplus à l'infoservice du
              // haut ou du bas, et il faut connaître les deux plages pour l'offrir.
              const prevEnd = canInsert && hasGapBefore ? nearestEndBelow(items, li.item.id, liStart) : null
              const gapBefore: AddGap | null = prevEnd !== null ? gapAt(prevEnd, liStart, prevEnd, liStart) : null
              const nextStart = canInsert && hasGapAfter && li.item.itemType !== 'INFOSERVICE'
                ? nearestStartAbove(items, li.item.id, liEnd)
                : null
              const gapAfter: AddGap | null = nextStart !== null ? gapAt(liEnd, nextStart, liEnd, nextStart) : null

              return (
                <AgendaBlock
                  key={li.item.id}
                  layoutItem={li}
                  isConflict={conflictSet.has(li.item.id)}
                  isSelected={li.item.id === selectedItemId}
                  isMultiSelected={isMultiSelected(li.item.id)}
                  isGroupDragging={groupDragIds?.has(li.item.id) ?? false}
                  isNext={li.item.id === nextItem?.id}
                  hasGapBefore={hasGapBefore}
                  hasGapAfter={hasGapAfter}
                  onSelect={onSelectItem}
                  onContextMenu={onItemContextMenu}
                  onAddAtTime={onAddAtTime}
                  dragLocked={dragLocked}
                  insertBefore={insertBefore}
                  insertAfter={insertAfter}
                  gapBefore={gapBefore}
                  gapAfter={gapAfter}
                  infoserviceResizeEdges={liveResize ? { start: false, end: true } : infoRun ? {
                    start: infoRun.firstId === li.item.id && infoRun.startMs >= visibleDayStartMs,
                    end: infoRun.lastId === li.item.id && infoRun.endMs <= visibleDayEndMs,
                  } : undefined}
                  onResizeInfoservice={liveResize ? handleLiveResize : infoRun ? handleInfoserviceResize : undefined}
                  resizeSubject={liveResize ? 'LIVE' : 'INFOSERVICE'}
                />
              )
            })}

            {nowLineY !== null && <NowLine y={nowLineY} label={nowLabel} />}
          </div>
        </div>
      </div>
    </DndContext>
  )
})
